/* eslint-disable import/no-unresolved */
/**
 * @name Allocations Hook
 * @memberof module:common/hooks
 * @type {ReactHook}
 * @return {React} Captable Hook
 */

// #region dependencies
import React, { useCallback, useContext, useEffect, useMemo, useState } from 'react';
// #endregion
// #region common
import { isEmpty } from 'underscore';
import { allocationsAction, globalAction, valuationsAction } from 'common/actions';
// #endregion
// #region service
import { ERROR_403 } from 'common/config/api';
import { ALLOCATION_STATUS_TITLE, ALLOCATIONS_DRAFT } from 'common/constants/allocations';
import { useStore } from 'common/store';
import { AllocationStatusBadge } from 'components';
import { LayoutContext } from 'context';
import { useAllocationValuesStore } from 'store';
import { extractResponseErrorMessage, getAllocationStatus, getNumberValue } from 'utilities';
import { AllocationService } from '..';
import { useResponse } from './useResponse';
import { Allocation, AllocationModel } from '../../api';
// #endregion

/**
 * @function
 * @name useGetAllocationInfo
 * @memberof Allocations Hook
 * @description Hook to fetch allocation info from allocations service
 * @param {INTEGER} allocationId the allocation id
 * @return {OBJECT} structure for store context.
 */
export const useGetAllocationInfo = (): [
  AllocationModel,
  (allocationId: number, exchangeRate: number) => Promise<AllocationModel | undefined>,
  (allocation: AllocationModel) => void
] => {
  const [, dispatch] = useStore();
  const [allocationInfo, setAllocationInfo] = useState<AllocationModel>();

  const { processErrorResponse, successNotification } = useResponse();

  const getAllocationInfo = useCallback(
    async (allocationId: number, exchangeRate: number): Promise<AllocationModel | undefined> => {
      const allocationService = new AllocationService();
      if (allocationId) {
        dispatch(globalAction.showLoadingProgress(true));

        try {
          const allocation = (await allocationService.dataById(
            allocationId,
            exchangeRate.toString()
          )) as AllocationModel;
          if (allocation) {
            // if weighted_share_values is not defined, we need it to be.
            allocation.weighted_share_values = allocation.weighted_share_values || [];
            setAllocationInfo(allocation);
            dispatch(globalAction.showLoadingProgress(false));

            return allocation;
          }
          successNotification('Allocation information loaded successfully');
        } catch (error: any) {
          const defaultErrorMessage
            = extractResponseErrorMessage(error)
            ?? 'An error occurred while loading the Allocation information';

          if (error.status === 403) {
            dispatch(allocationsAction.setAllocationInfo(ERROR_403));
          } else {
            processErrorResponse({ error, defaultErrorMessage });
          }
        } finally {
          dispatch(globalAction.showLoadingProgress(false));
        }
      }
    },
    [dispatch, processErrorResponse, successNotification]
  );

  useEffect(() => {
    dispatch(allocationsAction.setAllocationInfo(allocationInfo as unknown as Partial<Allocation>));
  }, [allocationInfo, dispatch]);

  return [allocationInfo as AllocationModel, getAllocationInfo, setAllocationInfo];
};

/**
 * @function
 * @name useUpdateAllocationInfo
 * @memberof Allocations Hook
 * @description Hook to update allocation info from allocations service
 * @param {INTEGER} dataId the allocation id
 * @param {OBJECT} allocationData allocation structure data
 * @return {OBJECT} structure for store context.
 */

export const useUpdateAllocationInfo = () => {
  const allocationService = new AllocationService();
  const [globalStore, dispatch] = useStore();
  const [updatedAllocation, setUpdatedAllocation] = useState<AllocationModel>();

  const { processErrorResponse, successNotification } = useResponse();

  const updateAllocation = async (newAllocationId: number, newAllocationData: AllocationModel) => {
    if (newAllocationId && newAllocationData) {
      dispatch(globalAction.showLoadingProgress(true));
      try {
        const data = await allocationService.updateDataById(newAllocationId, newAllocationData);

        dispatch(
          valuationsAction.setValuationInfo({
            ...globalStore.valuationInfo,
            allocation: data,
          })
        );

        setUpdatedAllocation(data as AllocationModel);
        successNotification('Allocation updated successfully');
        return data;
      } catch (error) {
        processErrorResponse({
          error,
          defaultErrorMessage: 'An error occurred while updating Allocation information',
          action: 'update allocation information',
        });
      } finally {
        dispatch(globalAction.showLoadingProgress(false));
      }
    }
  };
  return [updatedAllocation, updateAllocation];
};

export const useGetRiskFreeRate = () => {
  const { processErrorResponse } = useResponse();

  const getRiskFreeRateByDate = useCallback(
    async (maturity, queryDate) => {
      if (maturity && queryDate) {
        try {
          const allocationService = new AllocationService();
          const riskFreeRate = await allocationService.getRiskFreeRateByDate(maturity, queryDate);
          return riskFreeRate;
        } catch (error) {
          const defaultErrorMessage = 'An error occurred while trying to get risk free rate';
          processErrorResponse({
            error,
            defaultErrorMessage,
            action: 'get risk free rate',
          });
        }
      }
    },
    [processErrorResponse]
  );

  return [getRiskFreeRateByDate];
};

export const useGetAllocationStatusBreadcrumb = (allocationIdOverride?: number | string | null) => {
  const storedAllocationId = useAllocationValuesStore(state => state.currentAllocationId);
  const allocationId = getNumberValue(allocationIdOverride ?? storedAllocationId);
  const { companyExchangeRate } = useContext(LayoutContext);
  const [allocationInfo, getAllocationInfo] = useGetAllocationInfo();

  const reloadAllocationBreadcrumbInfo = useCallback(
    (
      nextAllocationId: number | string | null,
      nextCompanyExchangeRate: unknown
    ) => {
      const normalizedAllocationId = getNumberValue(nextAllocationId);
      const normalizedCompanyExchangeRate = getNumberValue(
        typeof nextCompanyExchangeRate === 'number' || typeof nextCompanyExchangeRate === 'string'
          ? nextCompanyExchangeRate
          : null
      );

      if (normalizedAllocationId && normalizedCompanyExchangeRate) {
        getAllocationInfo(normalizedAllocationId, normalizedCompanyExchangeRate);
      }
    },
    [getAllocationInfo]
  );

  // Fetch the legacy breadcrumb allocation status whenever route context changes.
  useEffect(() => {
    reloadAllocationBreadcrumbInfo(allocationId, companyExchangeRate);
  }, [allocationId, companyExchangeRate, reloadAllocationBreadcrumbInfo]);

  const allocationStatus = useMemo(() => {
    if (!isEmpty(allocationInfo)) {
      const { is_published: isPublished, is_final: isFinal } = allocationInfo;
      return getAllocationStatus({ isFinal: Boolean(isFinal), isPublished: Boolean(isPublished) });
    }
    return ALLOCATIONS_DRAFT;
  }, [allocationInfo]);

  const allocationStatusBreadcrumb = useMemo(
    () => ({
      title: ALLOCATION_STATUS_TITLE,
      icon: <AllocationStatusBadge status={allocationStatus} />,
    }),
    [allocationStatus]
  );

  return { allocationStatus, allocationStatusBreadcrumb, reloadAllocationBreadcrumbInfo };
};
